/**
 * Kabuk kayıt defteri ve eski router'a bağlanma noktası.
 *
 * Kritik nokta: core.js'teki go() fonksiyonuna BİR KEZ dokunuluyor. Ondan sonra
 * her yeni modül göçünde core.js'e hiç dokunmuyoruz — kabuk go()'yu sarıp
 * hangi view'a geçildiğini dinliyor ve ilgili modülü mount/unmount ediyor.
 *
 * Eski mimaride her sayfa go() içinde kendi if dalını istiyordu; sayfa silmek
 * o dalı da bulmayı gerektiriyordu. Bu sarmalayıcı o zorunluluğu bitiriyor.
 */

import type { WebFeature } from './feature.ts';

const features = new Map<string, WebFeature>();
const byView = new Map<string, WebFeature>();
let active: WebFeature | null = null;

export function register(feature: WebFeature): void {
  if (features.has(feature.id)) throw new Error(`özellik zaten kayıtlı: ${feature.id}`);
  if (feature.view) {
    const clash = byView.get(feature.view);
    if (clash) throw new Error(`view çakışması: ${feature.view} → ${clash.id} ve ${feature.id}`);
    byView.set(feature.view, feature);
  }

  features.set(feature.id, feature);

  // Eski kodun hâlâ ihtiyaç duyduğu global adları yayınla (geçici köprü)
  for (const [name, fn] of Object.entries(feature.legacyGlobals ?? {})) {
    (globalThis as Record<string, unknown>)[name] = fn;
  }
}

/** Sayfası olmayan özellikleri açılışta bir kez başlat. */
export function bootAll(): void {
  for (const f of features.values()) {
    if (f.view || !f.boot) continue;
    try { f.boot(); }
    catch (e) { console.error(`[kabuk] ${f.id} boot:`, e); }
  }
}

/** Bir özelliği tamamen çıkar — kaldırmanın çalışma zamanı karşılığı. */
export function unregister(id: string): void {
  const f = features.get(id);
  if (!f) return;
  if (active?.id === id) { f.unmount?.(); active = null; }
  if (!f.view) f.unmount?.();   // sayfasız özellikler de kendini toplasın
  for (const name of Object.keys(f.legacyGlobals ?? {})) {
    delete (globalThis as Record<string, unknown>)[name];
  }
  if (f.view) byView.delete(f.view);
  features.delete(id);
}

export const registered = () => [...features.values()];

/** İstenen view'ın modülü varsa göster, önceki modülü kapat. */
function activate(view: string): void {
  const next = byView.get(view) ?? null;
  if (active && active !== next) {
    try { active.unmount?.(); } catch (e) { console.error(`[kabuk] ${active.id} unmount:`, e); }
    active = null;
  }
  if (!next || active === next) return;

  const host = document.getElementById(view);
  if (!host) { console.error(`[kabuk] #${view} bulunamadı, ${next.id} bağlanamadı`); return; }

  active = next;
  try { next.mount?.({ host }); }
  catch (e) { console.error(`[kabuk] ${next.id} mount:`, e); }
}

/**
 * core.js'teki go()'yu sar. Eski davranış aynen çalışır, sonrasında kabuk
 * devreye girer. go() henüz tanımlı değilse tanımlanmasını bekler.
 */
export function attachToLegacyRouter(): void {
  const w = globalThis as Record<string, any>;

  const wrap = (original: (vid: string, nid: string) => void) =>
    function patched(this: unknown, vid: string, nid: string) {
      const r = original.apply(this, [vid, nid] as never);
      try { activate(vid); } catch (e) { console.error('[kabuk] activate:', e); }
      return r;
    };

  if (typeof w.go === 'function') {
    w.go = wrap(w.go);
  } else {
    // core.js henüz yüklenmemiş: tanımlandığı anda sarmalayıcıyı geçir
    let stored: ((vid: string, nid: string) => void) | undefined;
    Object.defineProperty(w, 'go', {
      configurable: true,
      get: () => stored,
      set: (fn) => { stored = typeof fn === 'function' ? wrap(fn) : fn; },
    });
  }

  // Açılışta zaten görünür olan view varsa onu da bağla
  const boot = () => {
    const on = document.querySelector('.vw.on');
    if (on?.id) activate(on.id);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
